<template>
	<div class="filter-container">
		<IconButton
			aria-label="filter options"
			iconName="sliders-h"
			iconStyle="far"
			size="large"
			@onClick="toggleFilterMenu"
		/>
		<span class="counter" data-test="counter" v-if="counter > 0">
			{{ counter }}
		</span>

		<transition name="fade">
			<div
				v-if="showFilterMenu"
				class="menu-container"
				data-test="menu-container"
			>
				<div class="menu-header">
					<h3 class="heading">Filters</h3>
					<TextButton
						aria-label="Reset filters"
						@onClick="handleResetButtonClick"
					>
						Reset
					</TextButton>
				</div>
				<FallBack v-if="loading">
					<LoadingIcon />
				</FallBack>
				<div v-else>
					<div
						v-for="(filter, index) in filterOptions"
						class="filter-options"
						:key="index"
					>
						<SingleFilter
							:filterReset="filterReset"
							:options="filter.data"
							:selectedFilters="getSelectedFilters(filter.type)"
							:type="filter.type"
							@onFilterChange="handleFilterChange"
							@clearReset="handleClearReset"
						/>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import { debounce } from 'lodash';

import IconButton from '@/components/global/IconButton.vue';
import TextButton from '@/components/global/TextButton.vue';
import SingleFilter from '@/components/global/SingleFilter.vue';
import LoadingIcon from '@/components/global/LoadingIcon.vue';
import FallBack from '@/components/global/FallBack.vue';

export default {
	name: 'MultipleFilters',
	props: {
		filterOptions: Array,
		loading: Boolean,
		selectedFilters: Object,
		// this will need to be implemented if we use this component in Client Connect Alerts
		defaultFilters: {
			type: Object,
			default: () => ({}),
		},
		onInit: Boolean,
		cancelFilterChange: Boolean,
	},
	components: {
		IconButton,
		TextButton,
		SingleFilter,
		LoadingIcon,
		FallBack,
	},
	data() {
		return {
			showFilterMenu: false,
			counter: 0,
			filters: {},
			filterReset: false,
		};
	},
	methods: {
		getSelectedFilters(type) {
			return this.selectedFilters[type] ? this.selectedFilters[type] : [];
		},
		toggleFilterMenu() {
			this.showFilterMenu = !this.showFilterMenu;
		},
		isSelected(type, filter) {
			if (this.filters[type] !== undefined) {
				return this.filters[type].findIndex((el) => el === filter.id) !== -1;
			}
		},
		handleFilterChange({ type, filter, toggleFilter = false }) {
			if (!this.filters[type]) {
				// this is needed to make new properties on filters reactive
				this.$set(this.filters, type, []);
			}
			if (toggleFilter) {
				this.filters[type] = filter;
			} else {
				for (const id of filter) {
					if (this.filters[type].includes(id)) {
						this.filters[type] = this.filters[type].filter((el) => el !== id);
						if (this.filters[type].length === 0) {
							delete this.filters[type];
						}
					} else {
						this.filters[type].push(id);
					}
				}
			}

			this.emitFilterChange();
			this.setCounter();
		},
		handleResetButtonClick() {
			this.filters = { ...this.defaultFilters };
			this.emitFilterChange();
			this.setCounter();
			this.filterReset = true;
		},
		handleClearReset() {
			this.filterReset = false;
		},
		setCounter() {
			this.counter = Object.keys(this.filters).length;
		},
		clickHandler(e) {
			if (!this.$el.contains(e.target) && this.showFilterMenu) {
				this.showFilterMenu = false;
			}
		},
		/**
		 * need nextTick() below because need to wait
		 * for this.selectedFilters to have a value
		 */
		async setFilters() {
			await this.$nextTick();
			const tempFilters = { ...this.selectedFilters };

			const filterKeys = Object.keys(tempFilters);
			filterKeys.forEach((key) => {
				if (tempFilters[key].length === 0) {
					delete tempFilters[key];
				}
			});

			this.filters = { ...tempFilters };
			this.setCounter();
		},
		emitFilterChange: debounce(function () {
			this.$emit('onFilterChange', {
				filters: this.filters,
			});
		}, 1000),
	},
	watch: {
		onInit(value) {
			if (value) {
				this.filters = { ...this.defaultFilters };
				this.counter = 0;
			}
		},
		cancelFilterChange(value) {
			if (value) {
				this.setFilters();
				this.$emit('onCancelFilterChange');
			}
		},
	},
	mounted() {
		window.addEventListener('click', this.clickHandler);
		this.setFilters();
	},
	beforeDestroy() {
		window.removeEventListener('click', this.clickHandler);
	},
};
</script>

<style scoped lang="scss">
.filter-container {
	position: relative;
}

.menu-container {
	box-sizing: border-box;
	background-color: $white;
	border-radius: $b360-border-radius-large;
	border: 1px solid $border-color-light;
	box-shadow: 0 3px 10px $shadow-color;
	z-index: 2;
	position: absolute;
	top: calc(100% + 10px);
	right: 0;
	opacity: 1;
	@include rem('width', 320px);
}

.heading {
	margin: 0;
	@include rem('font-size', 16px);
}

.menu-header {
	display: flex;
	@include rem('padding', 16px);
	justify-content: space-between;
	border-bottom: 1px solid $border-color-light;
}

.filter-options {
	@include rem('padding', 10px 16px);
	border-top: 1px solid $border-color-light;

	.button-container {
		@include rem('margin-top', 10px);

		.filter-button {
			@include rem('margin-right', 8px);
			@include rem('margin-bottom', 8px);
		}
	}
}

.counter {
	background: $brand-blue;
	color: $white;
	font-weight: bold;
	line-height: 1;
	position: absolute;
	top: -13px;
	right: -13px;
	border-radius: 6px;
	display: flex;
	justify-content: center;
	align-items: center;
	@include rem('font-size', 14px);
	@include rem('width', 26px);
	@include rem('height', 26px);
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease;
}

.fade-enter,
.fade-leave-active {
	opacity: 0;
}
</style>
